import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Quote, BookOpen, Megaphone, HandHeart } from "lucide-react";
import { api, fileUrl } from "@/lib/api";

const fallbackBoardMembers = [
  {
    name: "Wesley Bal",
    role: "President",
    image: "/board/wesley.jpg",
    inMemoryOf: { name: "Rachel Amber", relation: "Daughter", image: "/board/rachel.jpg" },
    bio: "I have lived in Dr Phillips, Orlando for 25 years. I am the father of two daughters. We lost Rachel Amber to Fentanyl poisoning on February 7, 2019, after many years of drug and alcohol use. She leaves behind a younger sister, Kristin, and her mother. Rachel was our hippie adventuresome butterfly. She loved life as she knew it. As a supporting member of PARS, I want to help advocate in her memory for those who suffer from SUD, Substance Use Disorder - and to educate on the dangers of street drugs, the presence of fentanyl and the opioid crisis in our community. Rachel did not plan on dying that day… Forever 33.",
  },
  {
    name: "RoseMarie Aldred",
    role: "Co-Founder · Vice President · Secretary",
    image: "/board/rosemarie.png",
    inMemoryOf: { name: "Andrew", relation: "Son", image: "/board/andrew.jpg" },
    bio: "My name is RoseMarie Aldred, I am a lifelong resident of Orlando. I have a business degree from Barry University. I am the mother of two children, Victoria and Andrew. Andrew was a loving, smart, athletic and compassionate young man, taken from us way too soon. He lost his battle to Substance Use Disorder (SUD) in January 2020. As founding board member of Project Addiction: Reversing the Stigma, it is my mission to help others through education, sharing my experience, and to take action through legislative changes.",
  },
  {
    name: "Dawn Ferrin",
    role: "Co-Founder · Treasurer",
    image: "/board/dawn.jpg",
    inMemoryOf: { name: "Lynda", relation: "Sister", image: "/board/lynda.jpg" },
    bio: "I am a co-founder of PARS - Project Addiction: Reversing the Stigma. After the sudden loss of my only sibling, Lynda, it seems that siblings are often left out of support and resources, especially adult siblings. Starting PARS, I feel that we can make an impact - not just for parents and/or children - but for the forgotten siblings.",
  },
  {
    name: "Diane Stevenson",
    role: "Co-Founder",
    image: "/board/diane.jpg",
    inMemoryOf: { name: "Tylar-Ann", relation: "Daughter", image: "/board/tylar-ann.jpg" },
    bio: "I am founding co-chairperson for PARS - Project Addiction: Reversing The Stigma. My husband Harry and I lost our beautiful daughter, Tylar-Ann, to an accidental Fentanyl overdose February 21, 2015. She was 26 years old. She leaves behind her older sister Nikki, 3 nephews, 1 niece, and brother-in-law, Nick. Since her passing it has become our mission to advocate in her memory for those who continue to suffer from Substance Use Disorder and Mental Illness.",
  },
  {
    name: "Donna Mina",
    role: "Co-Founder",
    image: "/board/donna.jpg",
    inMemoryOf: { name: "Anna", relation: "Daughter", image: "/board/anna.jpg" },
    bio: "My name is Donna Mina and I am a mother of 5 and grandmother of 11. On November 25th 2018 I lost my daughter Anna and my life was forever changed. Anna suffered from SUD and it is my life's mission to help people understand that SUD is a disease and not a choice.",
  },
];

const coreValues = [
  {
    icon: BookOpen,
    image: "/events/value-education.jpg",
    title: "Education",
    copy: "Through education, we bring a better understanding to the community about the correlation between SUD and Mental Illness, the signs of SUD, and how to get loved ones the help they need, quickly.",
  },
  {
    icon: Megaphone,
    image: "/events/value-awareness.jpg",
    title: "Awareness",
    copy: "Through social awareness we bring knowledge and understanding to the community about SUD and Mental Illness.",
  },
  {
    icon: HandHeart,
    image: "/events/value-support.jpg",
    title: "Support",
    copy: "Supporting other organizations who are like-minded in their mission.",
  },
];

const historyImages = [
  { src: "/events/event-golf.jpg", caption: "Recovery Connections of Central Florida Golf Fundraiser - September 12, 2021" },
  { src: "/events/event-apald.jpg", caption: "APALD Rally - June 4, 2021" },
  { src: "/events/event-jcc.jpg", caption: "Jewish Community Center Panel Discussion - May 27, 2021" },
  { src: "/events/event-rally.jpg", caption: "APALD Rally - June 4, 2021" },
];

export default function About() {
  const [boardMembers, setBoardMembers] = useState(fallbackBoardMembers);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/leaders");
        if (Array.isArray(data) && data.length > 0) {
          setBoardMembers(data.map((l) => ({
            name: l.name || "",
            role: l.role || "",
            image: fileUrl(l.image_path),
            inMemoryOf: { name: l.memory_name || "", relation: l.memory_relation || "", image: fileUrl(l.memory_image_path) },
            bio: l.bio || "",
          })));
        }
      } catch {}
    })();
  }, []);

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="bg-[var(--rts-blue-pale)] py-20 lg:py-28">
        <div className="max-w-6xl mx-auto px-6">
          <span className="inline-block text-xs font-bold tracking-[0.25em] uppercase text-[var(--rts-red)] mb-4">
            About PARS
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[var(--rts-ink)] leading-[1.05] mb-6">
            Stigma is the wall. <span className="italic text-[var(--rts-blue-deep)]">Story is the way through.</span>
          </h1>
          <p className="text-lg text-[var(--rts-ink-soft)] max-w-3xl leading-relaxed">
            Project Addiction: Reversing the Stigma (PARS) is a Florida-based nonprofit empowering the lives of those with Substance Use Disorder and Mental Illness through Education, Awareness, and Support.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="bg-white py-20" data-testid="mission-statement">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-4">
              <span className="inline-block text-xs font-bold tracking-[0.25em] uppercase text-[var(--rts-red)] mb-3">
                Our purpose
              </span>
              <h2 className="font-display text-3xl sm:text-4xl text-[var(--rts-ink)]">Mission Statement</h2>
            </div>
            <div className="md:col-span-8 space-y-5 text-[var(--rts-ink-soft)] leading-relaxed">
              <p className="text-xl sm:text-2xl font-display text-[var(--rts-ink)] leading-snug">
                Empowering the lives of those with Substance Use Disorder (SUD) and Mental Illness through <span className="text-[var(--rts-blue-deep)] italic">Education, Awareness, and Support.</span>
              </p>
              <div className="border-l-4 border-[var(--rts-red)] pl-5 py-2 bg-[var(--rts-blue-pale)]/40 rounded-r-lg">
                <Quote className="w-5 h-5 text-[var(--rts-red)] mb-1" />
                <p className="italic text-[var(--rts-ink)]">
                  "We often form stronger bonds with those who have suffered the same loss as us."
                </p>
                <p className="mt-2 text-xs uppercase tracking-widest font-bold text-[var(--rts-blue-deep)]">- Narin Grewal</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision */}
      <section className="bg-[var(--rts-blue-pale)] py-20" data-testid="vision-section">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <span className="inline-block text-xs font-bold tracking-[0.25em] uppercase text-[var(--rts-red)] mb-3">
              Looking forward
            </span>
            <h2 className="font-display text-4xl sm:text-5xl text-[var(--rts-ink)] mb-5">Vision</h2>
            <p className="text-[var(--rts-ink-soft)] leading-relaxed text-base">
              Project Addiction: Reversing the Stigma (PARS) envisions a community where the stigma of SUD and Mental Illness is reversed through education, bringing awareness, and supporting the efforts of other like-minded organizations in our community.
            </p>
          </div>
          <div className="lg:col-span-7">
            <img
              src="/events/vision.jpg"
              alt="A serene horizon - a vision of community and healing"
              className="w-full rounded-2xl shadow-xl object-cover aspect-[16/8]"
              data-testid="vision-image"
            />
          </div>
        </div>
      </section>

      {/* History */}
      <section className="bg-white py-20" data-testid="history-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-3xl mb-12">
            <span className="inline-block text-xs font-bold tracking-[0.25em] uppercase text-[var(--rts-red)] mb-3">
              Our story
            </span>
            <h2 className="font-display text-4xl sm:text-5xl text-[var(--rts-ink)] mb-5">History</h2>
            <p className="text-lg text-[var(--rts-ink-soft)] leading-relaxed">
              United by a common tragedy, Project Addiction: Reversing The Stigma (PARS) was founded to spread awareness of the Opioid epidemic and find proactive ways to honor family members lost to Substance Use Disorder and mental illness.
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {historyImages.map((img) => (
              <figure key={img.src} className="group" data-testid="history-event">
                <div className="overflow-hidden rounded-xl aspect-[4/3] bg-[var(--rts-blue-pale)]">
                  <img
                    src={img.src}
                    alt={img.caption}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <figcaption className="mt-2 text-xs text-[var(--rts-muted)] leading-snug">{img.caption}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="bg-[var(--rts-ink)] text-white py-20" data-testid="core-values">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-xs font-bold tracking-[0.25em] uppercase text-[var(--rts-blue-light)] mb-3">
              What guides us
            </span>
            <h2 className="font-display text-4xl sm:text-5xl mb-3">Core Values</h2>
            <p className="text-white/70">Three principles at the heart of everything PARS stands for.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {coreValues.map((v) => (
              <article key={v.title} className="bg-white/5 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/10 hover:border-[var(--rts-blue)] transition" data-testid={`value-${v.title.toLowerCase()}`}>
                <div className="aspect-[16/9] overflow-hidden bg-[var(--rts-blue-deep)]">
                  <img src={v.image} alt={v.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <div className="w-11 h-11 rounded-xl bg-[var(--rts-red)] grid place-items-center mb-4">
                    <v.icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-display text-2xl mb-2">{v.title}</h3>
                  <p className="text-white/75 text-sm leading-relaxed">{v.copy}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Board Members */}
      <section className="bg-white py-20" data-testid="board-members">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-xs font-bold tracking-[0.25em] uppercase text-[var(--rts-red)] mb-3">
              Leadership
            </span>
            <h2 className="font-display text-4xl sm:text-5xl text-[var(--rts-ink)]">Our Leaders</h2>
            <p className="mt-4 text-[var(--rts-ink-soft)]">Parents, siblings, and advocates - united by loss, driven by purpose.</p>
          </div>

          <div className="space-y-12">
            {boardMembers.map((m, i) => (
              <article
                key={m.name}
                className={`grid md:grid-cols-12 gap-6 md:gap-10 items-center ${i % 2 === 1 ? "md:[direction:rtl]" : ""}`}
                data-testid={`board-member-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="md:col-span-5 [direction:ltr]">
                  <div className="grid grid-cols-2 gap-3">
                    <figure>
                      <div className="aspect-square overflow-hidden rounded-2xl ring-4 ring-[var(--rts-blue-pale)]">
                        <img src={m.image} alt={`${m.name}, ${m.role}`} className="w-full h-full object-cover" />
                      </div>
                      <figcaption className="mt-2 text-center">
                        <div className="font-display text-lg text-[var(--rts-ink)] font-bold">{m.name}</div>
                        <div className="text-xs uppercase tracking-widest text-[var(--rts-blue-deep)] font-semibold">{m.role}</div>
                      </figcaption>
                    </figure>
                    <figure>
                      <div className="aspect-square overflow-hidden rounded-2xl ring-4 ring-[var(--rts-red)]/20">
                        <img src={m.inMemoryOf.image} alt={`In loving memory of ${m.inMemoryOf.name}, ${m.inMemoryOf.relation}`} className="w-full h-full object-cover" />
                      </div>
                      <figcaption className="mt-2 text-center">
                        <div className="font-display text-lg text-[var(--rts-ink)] font-bold">{m.inMemoryOf.name}</div>
                        <div className="text-xs uppercase tracking-widest text-[var(--rts-red)] font-semibold">{m.inMemoryOf.relation}</div>
                      </figcaption>
                    </figure>
                  </div>
                </div>
                <div className="md:col-span-7 [direction:ltr]">
                  <div className="border-l-4 border-[var(--rts-red)] pl-6 py-2">
                    <p className="text-[var(--rts-ink-soft)] leading-relaxed">{m.bio}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <p className="text-center mt-16 font-display text-xl sm:text-2xl italic text-[var(--rts-blue-deep)]">
            "Finding peace in our grief."
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[var(--rts-blue-deep)] text-white py-16">
        <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl mb-2">Ready to join us?</h2>
            <p className="text-white/80">Reach out - every conversation moves the mission forward.</p>
          </div>
          <Link to="/connect-with-us" className="inline-flex items-center gap-2 bg-[var(--rts-red)] hover:bg-[var(--rts-red-deep)] px-7 py-3.5 rounded-full font-bold shadow-lg transition hover:-translate-y-0.5" data-testid="about-cta-connect">
            Connect with Us <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
